import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpResrcPlanResultService from "@plannel/services/fp/fp-resrc-plan-result-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup, { BUCKET_CASE } from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import FpGridSetting, {
  readHide, createSumAwareFormatter, buildGroupColumnPair, REPORT_TREE_GRID_OPTIONS, buildReportStatusBar,
  applyColumnOrder, buildDayBucketPivotColumnDefs, consumePendingFocus
} from "@plannel/pages/factory-plan/common/grid";
import { buildXAxisSortMap } from "@plannel/pages/factory-plan/common/chart";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import { getFilterStore } from "@plannel/components/aggrid/FilterStatusPanel";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import SplitterContainer from "@plannel/components/SplitterContainer";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — planresult/PlanResult와 동일한 값(9)을 그대로 쓴다.
// 화면/planScope별로 다른 값이 아니라 조회조건 세팅 3종(버킷/검색필드 표시/검색구간)을 정적 구성으로
// 전환하는 결정은 Report 카테고리 공통(fp-report-screen-guide.md §A-3~§A-3d)이다.
// TODO(MIG-T2): [resrcplanresult/ResrcPlanResult] 조회조건 세팅 관련 테넌트별 커스터마이징 미이관.
//   old: setVersionPeriod()가 setting(FP_SRH_RANGE.attr01Val)로 DB에서 검색구간을 조회
//   사유: 정적 구성(화면 상수)으로 전환 — 가이드 §A-3c에서 결정된 값(9)을 그대로 재사용
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup은 화면에 렌더링만 되고(ref 생성, onKeyDown 배선) loadData/loadChartData
// 어디서도 searchOptGroupRef.current.getValues()를 호출하지 않는다(old 소스 재확인, PlanResult와의
// 차이점) — 즉 실제로 백엔드에 전달되는 자유 텍스트 검색 필드가 하나도 없다. 백엔드
// (ResrcPlanResultQueryRepository.makeWhereClause)도 resourceCdNm류 텍스트 파라미터를 읽지 않는다.
// 그래서 이 화면에는 SearchOptsGroupRetrieve를 두지 않는다(§A-3b 원칙 — "old가 실제로 쓰는 필드"
// 기준으로 고정 카탈로그를 정하는데, 이 화면은 그 집합이 공집합이다).

// 백엔드가 합계 행에 넣을 수 있는 표식(old styleCallback이 5개 차원 중 하나라도 'SUM'이면
// summary-col 스타일을 적용) — 현재 ResrcPlanResultCrosstabDto.merge()는 미매칭 레벨을 ""로만
// 채우고 'SUM' 문자열을 쓰지 않지만, old와 동일하게 방어적으로 남겨둔다(PlanResult와 동일 판단).
const SUM_ROW = "SUM";

const PARAM_DATE_FORMAT = "yyyyMMdd";

// 트리(행 그룹) 계층 — 공장 > 스테이지 > 자원 > 공정그룹, 4레벨만 행 그룹이다. 공정(routeCd/
// routeNm)은 old에서 mergeRule이 걸려 있었지만(위 5레벨 전부 mergeRule), 실제 백엔드 merge
// 로직(ResrcPlanResultCrosstabDto.merge, GroupingLevel.PLANT_STAGE_RESOURCE_ROUTEGRP_ROUTE)의
// 그룹핑 키가 (plant,stage,resource,routeGrp,route,header)까지라 **공정이 이 화면 데이터의
// 최소 행 단위(그레인)** — 같은 공정 값을 가진 행이 여러 개로 쪼개지는 경우가 없다(2026-08-11
// 검토, 사람 지적으로 발견). 행 그룹은 "여러 leaf가 접혔다 펼쳐지는" 것이 의미가 있는 레벨에만
// 걸어야 하는데, 공정은 접혀도 항상 leaf 1개뿐이라 트리로 만들 이유가 없다 — analysisIndicator를
// leaf 필드로 두는 다른 Report 화면(ResourceUtilization 등)과 동일한 판단. 그래서 routeCd/routeNm은
// 행 그룹에서 빼고 ResProdPlan.js의 itemCd/itemNm 리프 패턴(CustomDetailHeader 직접 사용, rowGroup
// 아님)으로 내린다.
const GROUP_FIELDS = [
  "plantCd", "plantNm", "stageCd", "stageNm", "resourceCd", "resourceNm", "routeGrpCd", "routeGrpNm"
];

// 그룹 표시 컬럼의 colId. 명칭 컬럼은 p13n(사용자 컬럼설정 저장) 대상에서 뺀다(§A-6b).
const PLANT_CD_COL_ID = "plantCdGrp";
const PLANT_NM_COL_ID = `PLANT_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const STAGE_CD_COL_ID = "stageCdGrp";
const STAGE_NM_COL_ID = `STAGE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const RESOURCE_CD_COL_ID = "resourceCdGrp";
const RESOURCE_NM_COL_ID = `RESOURCE_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
const ROUTE_GRP_CD_COL_ID = "routeGrpCdGrp";
const ROUTE_GRP_NM_COL_ID = `ROUTEGRP_GRP_NM${GridUtils.IGNORE_P13N_COLLID}`;
// routeCd는 행 그룹이 아니라 leaf라 colId도 field 그대로 쓴다(ResProdPlan.js의 itemCd와 동일 관례).
const ROUTE_CD_COL_ID = "routeCd";
const ROUTE_NM_COL_ID = `ROUTE_NM${GridUtils.IGNORE_P13N_COLLID}`;

// CustomDetailHeader가 "코드 컬럼 화살표 → 명칭 컬럼 표시/숨김" 상태를 redux viewState에 저장할 때
// 쓰는 키 — 5쌍이 서로 다른 키를 써야 상태를 덮어쓰지 않는다(PlanResult와 동일 이유).
const PLANT_NM_HIDE_KEY = "plantNmColsHide";
const STAGE_NM_HIDE_KEY = "stageNmColsHide";
const RESOURCE_NM_HIDE_KEY = "resourceNmColsHide";
const ROUTE_GRP_NM_HIDE_KEY = "routeGrpNmColsHide";
const ROUTE_NM_HIDE_KEY = "routeNmColsHide";

const ResrcPlanResult = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const [chartData, setChartData] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const latestGridReqRef = useRef(0);
  const latestChartReqRef = useRef(0);
  // old prevChartParamsRef — 같은 (plant, stage, resource, routeGrp, route) 조합이면 재조회하지 않는다.
  const prevChartParamsRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const searchStateRef = useRef({ versionState, periodState });
  searchStateRef.current = { versionState, periodState };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveResrcPlanResult(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  useEffect(() => () => getFilterStore(viewName).clear(), [viewName]);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      // 합계(SUM) 행 강조 — old styleCallback은 5개 차원(plant/stage/resource/routeGrp/route) 중
      // 하나라도 'SUM'이면 summary-col 스타일을 적용했다. 현재 백엔드는 이 표식을 만들지 않지만
      // PlanResult와 동일하게 방어적으로 유지한다.
      getRowStyle: (params) => {
        const data = params.data;
        if (!data) return null;
        const isSum = [data.plantCd, data.stageCd, data.resourceCd, data.routeGrpCd, data.routeCd]
          .some(v => v === SUM_ROW);
        return isSum ? { backgroundColor: grey[100] } : null;
      },
      ...REPORT_TREE_GRID_OPTIONS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sumAwareFormatter = createSumAwareFormatter(SUM_ROW, t);

  // 고정(왼쪽 pinned) 컬럼 — old gridItems와 1:1(plantCd/plantNm/stageCd/stageNm/resourceCd/
  // resourceNm/routeGrpCd/routeGrpNm/routeCd/routeNm/displayColor(hidden)/sum, 총 12개).
  // 공장/스테이지/자원/공정그룹만 행 그룹, routeCd/routeNm(공정)은 leaf(위 GROUP_FIELDS 주석 참고).
  const fixedColumnDefs = useMemo(() => [
    ...GROUP_FIELDS.map(field => ({
      headerName: field, field, colId: field, rowGroup: true, hide: true, suppressColumnsToolPanel: true
    })),

    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, PLANT_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: PLANT_CD_COL_ID, cdField: "plantCd", cdHeader: "FP_PLANT_CD",
      nmColId: PLANT_NM_COL_ID, nmField: "plantNm", nmHeader: "PLANT_NM",
      hideKey: PLANT_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, STAGE_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: STAGE_CD_COL_ID, cdField: "stageCd", cdHeader: "FP_STAGE_CD",
      nmColId: STAGE_NM_COL_ID, nmField: "stageNm", nmHeader: "FP_STAGE_NM",
      hideKey: STAGE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, RESOURCE_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: RESOURCE_CD_COL_ID, cdField: "resourceCd", cdHeader: "FP_RESOURCE_CD",
      nmColId: RESOURCE_NM_COL_ID, nmField: "resourceNm", nmHeader: "FP_RESOURCE_NM",
      hideKey: RESOURCE_NM_HIDE_KEY, width: 150
    }),
    ...buildGroupColumnPair({
      viewName, hidden: readHide(reduxViewState, ROUTE_GRP_NM_HIDE_KEY), valueFormatter: sumAwareFormatter,
      cdColId: ROUTE_GRP_CD_COL_ID, cdField: "routeGrpCd", cdHeader: "FP_ROUTE_GRP_CD",
      nmColId: ROUTE_GRP_NM_COL_ID, nmField: "routeGrpNm", nmHeader: "FP_ROUTE_GRP_NM",
      hideKey: ROUTE_GRP_NM_HIDE_KEY, width: 150
    }),
    // leaf(공정) — 그룹이 아니라 실제 행의 값(위 GROUP_FIELDS 주석 참고, ResProdPlan.js의 itemCd와
    // 동일 패턴). buildGroupColumnPair(showRowGroup+agGroupCellRenderer)는 rowGroup 필드 전용이라
    // 여긴 CustomDetailHeader를 직접 붙인 평범한 필드 컬럼 쌍으로 내린다.
    {
      headerName: "FP_ROUTE_CD", field: "routeCd", colId: ROUTE_CD_COL_ID, width: 150,
      pinned: "left", lockPinned: true, type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter,
      headerComponent: CustomDetailHeader,
      headerComponentParams: {
        viewName, colsHide: readHide(reduxViewState, ROUTE_NM_HIDE_KEY), reduxKey: ROUTE_NM_HIDE_KEY, detailColumns: [ROUTE_NM_COL_ID]
      }
    },
    {
      headerName: "FP_ROUTE_NM", field: "routeNm", colId: ROUTE_NM_COL_ID, width: 150, pinned: "left", lockPinned: true,
      hide: readHide(reduxViewState, ROUTE_NM_HIDE_KEY), type: ["nonEditableColumn"], valueFormatter: sumAwareFormatter
    },

    // old headerText는 공백 1칸 + visible:false — 차트 색상(displayColor)에 쓰이므로 데이터는 유지한다.
    { headerName: " ", field: "displayColor", colId: "displayColor", hide: true },
    { headerName: "SUM", field: "sum", colId: "sum", width: 100, pinned: "left", lockPinned: true, type: ["numberColumn", "rightAligned"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  // 일버킷일 때 자식 헤더로 오는 요일 코드 번역+토/일 강조색은 common/grid.js
  // buildDayBucketPivotColumnDefs가 처리한다(PlanResult와 동일 처리, §A-6d/§A-6f).
  const buildPivotColumnDefs = (crosstabHeader) => buildDayBucketPivotColumnDefs(crosstabHeader, t);

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다.
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {
      plantCd: row.plantCd, plantNm: row.plantNm,
      stageCd: row.stageCd, stageNm: row.stageNm,
      resourceCd: row.resourceCd, resourceNm: row.resourceNm,
      routeGrpCd: row.routeGrpCd, routeGrpNm: row.routeGrpNm,
      routeCd: row.routeCd, routeNm: row.routeNm,
      displayColor: row.displayColor, sum: row.sum
    };
    (crosstabHeader ?? []).forEach((header, index) => {
      flat[header] = row.pivotData?.[index];
    });
    return flat;
  });

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const buildBaseParams = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      bucketType: period.bucketType,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };
  };

  const clearChart = () => {
    prevChartParamsRef.current = null;
    setChartData([]);
    setChartSeries([]);
    setChartLabels([]);
    getFilterStore(viewName).clear();
  };

  // 하단 상태바 브레드크럼 — FilterStatusPanel의 고정 슬롯(originLoc/destLoc/customer/item/
  // workCenter/resource)에 공장>스테이지>자원>공정그룹>공정 순서로 매핑한다(슬롯 이름은 RP/DP/MP
  // 어휘라 FP 도메인과 안 맞지만, 라벨은 header 값으로 직접 넣으므로 화면 표시는 정확하다 — §A-7).
  // resource 슬롯(6번째)은 5레벨뿐이라 비워둔다.
  const updateStatusBreadcrumb = (target) => {
    getFilterStore(viewName).setValue({
      originLocCd: target.plantCd ?? null, originLocCdHeader: t("FP_PLANT_CD"),
      originLocNm: target.plantNm ?? null, originLocNmHeader: t("PLANT_NM"),
      destLocCd: target.stageCd ?? null, destLocCdHeader: t("FP_STAGE_CD"),
      destLocNm: target.stageNm ?? null, destLocNmHeader: t("FP_STAGE_NM"),
      customerCd: target.resourceCd ?? null, customerCdHeader: t("FP_RESOURCE_CD"),
      customerNm: target.resourceNm ?? null, customerNmHeader: t("FP_RESOURCE_NM"),
      itemCd: target.routeGrpCd ?? null, itemCdHeader: t("FP_ROUTE_GRP_CD"),
      itemNm: target.routeGrpNm ?? null, itemNmHeader: t("FP_ROUTE_GRP_NM"),
      workCenterCd: target.routeCd ?? null, workCenterCdHeader: t("FP_ROUTE_CD"),
      workCenterNm: target.routeNm ?? null, workCenterNmHeader: t("FP_ROUTE_NM")
    });
  };

  // 클릭 기준으로 차트를 다시 조회한다. target에는 클릭한 레벨까지의 필드만 실려 있다(상위
  // 레벨을 클릭하면 하위 코드는 없음 — old updateChartFromGrid와 동일).
  const loadChart = (target) => {
    const { versionState: version } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;

    updateStatusBreadcrumb(target);

    const nextParams = {
      plantCd: target.plantCd ?? "", stageCd: target.stageCd ?? "", resourceCd: target.resourceCd ?? "",
      routeGrpCd: target.routeGrpCd ?? "", routeCd: target.routeCd ?? ""
    };
    const prev = prevChartParamsRef.current;
    if (prev && prev.plantCd === nextParams.plantCd && prev.stageCd === nextParams.stageCd
      && prev.resourceCd === nextParams.resourceCd && prev.routeGrpCd === nextParams.routeGrpCd
      && prev.routeCd === nextParams.routeCd) {
      return;
    }
    prevChartParamsRef.current = nextParams;

    const reqId = ++latestChartReqRef.current;
    const params = { ...buildBaseParams(), ...nextParams, useGrouping: true };

    // TODO(MIG-T2): [resrcplanresult/ResrcPlanResult] 그리드 컬럼 필터(값 목록 체크박스) 미이관.
    //   old: plantCd/stageCd/resourceCd/routeGrpCd/routeCd에 RealGrid autoFilter,
    //        onFilteringChanged가 현재 필터된 행 기준으로 차트를 다시 그림
    //   사유: 행 그룹(트리)으로 전환하면서 코드 컬럼이 hide:true인 데이터 컬럼이 돼 헤더 필터
    //        아이콘을 띄울 자리가 없어졌다(§A-6e와 동일 사유). 이 화면은 PlanResult와 달리
    //        백엔드(resrc-plan-result)가 changes 파라미터 자체를 읽지 않아 2차에도 백엔드 작업이
    //        추가로 필요하다.
    fpResrcPlanResultService.getResrcPlanResultChart(params)
      .then(response => {
        if (reqId !== latestChartReqRef.current) return;

        const { chartHeader, data } = response.data ?? {};
        const rows = data ?? [];

        if (isEmpty(rows) || isEmpty(chartHeader)) {
          setChartData([]);
          setChartSeries([]);
          setChartLabels([]);
          return;
        }

        // 백엔드(ResrcPlanResultCrosstabDto.merge + GroupingLevel)가 요청한 target의 레벨까지
        // 이미 집계를 끝내서 항상 1행만 내려준다(PlanResult처럼 아이템별로 여러 행이 나뉘는
        // 구조가 아니다) — 품목 차원이 없는 화면이라 시리즈는 "투입 수량" 단일 measure 하나뿐.
        const row = rows[0];
        const measureLabel = t("FP_INPUT_QTY");

        setChartLabels(chartHeader);
        const point = { measure: measureLabel };
        chartHeader.forEach((header, index) => { point[header] = row.pivotData?.[index] ?? 0; });
        setChartData([point]);
        setChartSeries([{
          field: measureLabel, text: measureLabel, type: "bar", groupName: t("QTY"),
          typeOptions: { stackId: "resrcPlanResult", fill: row.displayColor }
        }]);
      })
      .catch(e => {
        prevChartParamsRef.current = null;
        debug(e);
      });
  };

  const retrieveResrcPlanResult = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestGridReqRef.current;
    const params = { ...buildBaseParams(), plantCds: version.plantCd, useGrouping: false };

    gridRef.current?.api?.setGridOption("loading", true);

    fpResrcPlanResultService.getResrcPlanResults(params)
      .then(response => {
        if (reqId !== latestGridReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        const api = gridRef.current?.api;
        if (!api) return;

        const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(crosstabHeader)];
        GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
        applyColumnOrder(api, nextColumnDefs);

        const rows = buildRowData(crosstabHeader, data);
        setRowData(rows);

        if (isEmpty(rows)) {
          clearChart();
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          return;
        }

        // old: 조회 직후 첫 행의 routeCd 컬럼에 포커스를 옮기고, 그 행의 5개 차원 전체(가장 깊은
        // 레벨까지)로 차트를 그린다(gridView.setCurrent({itemIndex:0, column:'routeCd'}) +
        // setChart(...gridView.getValues(0))). PlanResult(첫 행의 plant 레벨만 사용)와 다르다.
        pendingFocusRef.current = { rowIndex: 0, colId: ROUTE_CD_COL_ID };
        prevChartParamsRef.current = null;
        loadChart(rows[0]);
      })
      .catch(e => {
        if (reqId === latestGridReqRef.current) clearChart();
        debug(e);
      })
      .finally(() => {
        if (reqId === latestGridReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  const onRowDataUpdated = () => consumePendingFocus(gridRef, pendingFocusRef);

  // 클릭 컬럼 → 드릴다운 레벨. old updateChartFromGrid의 column.startsWith('plant'|'stage'|
  // 'resource'|'routeGrp') 분기를 트리 표시 컬럼 colId 기준으로 옮긴 것 — route 레벨(또는 그
  // 이상, pivot/sum 셀)은 fallback으로 전체 row를 반환한다(old의 나머지 분기 없음과 동일 결과).
  const PLANT_LEVEL_COL_IDS = new Set([PLANT_CD_COL_ID, PLANT_NM_COL_ID]);
  const STAGE_LEVEL_COL_IDS = new Set([STAGE_CD_COL_ID, STAGE_NM_COL_ID]);
  const RESOURCE_LEVEL_COL_IDS = new Set([RESOURCE_CD_COL_ID, RESOURCE_NM_COL_ID]);
  const ROUTE_GRP_LEVEL_COL_IDS = new Set([ROUTE_GRP_CD_COL_ID, ROUTE_GRP_NM_COL_ID]);

  const resolveChartTarget = (params) => {
    const node = params.node;
    if (!node) return null;

    if (node.group) {
      const target = {};
      for (let current = node; current; current = current.parent) {
        if (current.field) target[current.field] = current.key;
      }
      return isEmpty(target) ? null : target;
    }

    const row = node.data;
    if (!row) return null;

    const colId = params.column?.getColId() ?? "";
    if (PLANT_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm };
    }
    if (STAGE_LEVEL_COL_IDS.has(colId)) {
      return { plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm };
    }
    if (RESOURCE_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        resourceCd: row.resourceCd, resourceNm: row.resourceNm
      };
    }
    if (ROUTE_GRP_LEVEL_COL_IDS.has(colId)) {
      return {
        plantCd: row.plantCd, plantNm: row.plantNm, stageCd: row.stageCd, stageNm: row.stageNm,
        resourceCd: row.resourceCd, resourceNm: row.resourceNm, routeGrpCd: row.routeGrpCd, routeGrpNm: row.routeGrpNm
      };
    }
    return row;
  };

  const onCellClicked = (params) => {
    const target = resolveChartTarget(params);
    if (target) loadChart(target);
  };

  const statusBar = useMemo(() => buildReportStatusBar(viewName), [viewName]);

  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = addDays(startDate, SEARCH_ZONE_DAYS);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    setRowData([]);
    clearChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    periodState.startDate, periodState.endDate, periodState.bucketType, periodState.isEntirePlanPeriod,
    versionState.plantCd
  ]);

  // x축(주+월 등 버킷이 섞이면) 기본 사전순 정렬을 chartLabels 원래 순서로 덮어쓴다
  // (common/chart.js, fp-report-screen-guide.md §A-7 — 크로스탭 차트 화면 공통).
  const pivotConfig = useMemo(() => ({
    groupKey: "measure",
    chartSeries,
    include: chartLabels,
    xAxisSortMap: buildXAxisSortMap(chartLabels)
  }), [chartSeries, chartLabels]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          bucketCase={BUCKET_CASE.WITH_HOUR}
        />
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <SplitterContainer initialSizes={[1, 1]}>
        <Box id="fp-resrc-plan-result-grid" className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 8px)", pb: 1 }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            {...defaultGridMemo}
            statusBar={statusBar}
            onGridReady={onGridReady}
            onRowDataUpdated={onRowDataUpdated}
            onCellClicked={onCellClicked}
          />
        </Box>
        <Box className="chart-area" sx={{ height: "calc(100% - 8px)", mt: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* old ResrcPlanResultChart.jsx는 ChartLegendPlugin을 실제로 plugins에 등록해 범례를
              보여줬다(PlanResult와 달리 이 화면은 old에도 범례가 있었다) — recharts 기본
              <Legend/>를 CSS로 숨기지 않는다. 다만 old 범례의 "클릭 시 series 숨김+취소선" 같은
              장식성 인터랙션은 대응 훅이 없어 Tier2로 유보한다(§A-7 카탈로그, 아래 TODO 참고). */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {/* TODO(MIG-T2): [resrcplanresult/ResrcPlanResult] 차트 범례 클릭 시 series 숨김+취소선 미이관.
                  old: ChartLegendPlugin(DOM 커스텀 범례, 클릭 시 토글+취소선)
                  사유: recharts 기본 Legend는 클릭 토글을 지원하지 않고, PivotChartComposed에도
                       대응 훅이 없다 — 1차 목표(조회·표시)에 불필요, §A-7에서 Report 카테고리
                       공통 Tier2로 확정(화면마다 재검토하지 않음, 항상 쓰이는 기능이라 예외 승격 불필요) */}
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: "12px" }, minHeight: "0px", decimalPoint: true }}
              showConfigurationPopup={false}
              nullConvert
            />
          </Box>
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ResrcPlanResult);
